import { useState, useRef } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Mail, Upload, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CHUNK_SIZE = 8 * 1024 * 1024;  // 8 MB per chunk — well under the 12MB server cap

function genUploadId() {
  // 32-char hex, alphanum
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function fmtSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function ChunkedMboxUploader() {
  const [status, setStatus] = useState(null);       // 'uploading' | 'finalizing' | 'done' | 'error'
  const [progress, setProgress] = useState(0);       // 0–100
  const [fileMeta, setFileMeta] = useState(null);
  const [result, setResult] = useState(null);
  const uploadIdRef = useRef(null);
  const cancelRef = useRef(false);

  const reset = () => {
    setStatus(null); setProgress(0); setFileMeta(null); setResult(null);
    uploadIdRef.current = null; cancelRef.current = false;
  };

  const handleFile = async (file) => {
    if (!file) return;
    setResult(null);
    cancelRef.current = false;
    const uploadId = genUploadId();
    uploadIdRef.current = uploadId;
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    setFileMeta({ name: file.name, size: file.size, total_chunks: totalChunks });
    setStatus("uploading");

    try {
      for (let i = 0; i < totalChunks; i++) {
        if (cancelRef.current) {
          await api.post("/admin/ahj/mbox/abort", new URLSearchParams({ upload_id: uploadId }));
          setStatus("error"); toast.info("Upload cancelled");
          return;
        }
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(totalChunks));
        fd.append("file", blob, `chunk_${i}.bin`);
        await api.post("/admin/ahj/mbox/chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // Finalize
      setStatus("finalizing");
      const { data } = await api.post(
        "/admin/ahj/mbox/finalize",
        new URLSearchParams({ upload_id: uploadId }),
        { timeout: 300000 },
      );
      setResult(data);
      setStatus("done");
      toast.success(
        `Parsed ${data.messages_scanned} messages · ${data.gov_contacts_found} contacts · ${data.messages_stored} threads stored`
      );
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Upload failed";
      toast.error(detail);
      setStatus("error");
      // Best-effort cleanup
      try {
        await api.post("/admin/ahj/mbox/abort",
          new URLSearchParams({ upload_id: uploadId }));
      } catch (_e) { /* ignore */ }
    }
  };

  const cancel = () => { cancelRef.current = true; };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="mbox-chunked-panel">
      <h3 className="font-outfit text-lg font-semibold text-slate-900 flex items-center gap-2">
        <Mail className="w-4 h-4 text-indigo-600" />Gmail Takeout .mbox upload (chunked)
      </h3>
      <p className="text-xs text-slate-500 mt-1 max-w-2xl">
        Upload large Gmail Takeout .mbox files (no 150&nbsp;MB proxy timeout). The file is sliced into 8&nbsp;MB
        chunks, streamed to the server, then reassembled and parsed. Up to 1&nbsp;GB total.
      </p>

      {status === null && (
        <label className="cursor-pointer inline-block mt-3">
          <input
            type="file"
            accept=".mbox,application/mbox"
            className="hidden"
            data-testid="mbox-chunked-input"
            onChange={(e) => { const f = e.target.files?.[0]; handleFile(f); e.target.value = ""; }}
          />
          <Button asChild data-testid="mbox-chunked-btn" variant="outline" className="pointer-events-none">
            <span><Upload className="w-4 h-4 mr-2" />Choose .mbox file</span>
          </Button>
        </label>
      )}

      {fileMeta && status !== null && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="mbox-chunked-progress">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{fileMeta.name}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                {fmtSize(fileMeta.size)} · {fileMeta.total_chunks} chunk{fileMeta.total_chunks === 1 ? "" : "s"}
              </div>
            </div>
            <div className="text-xs">
              {status === "uploading" && <span className="font-mono text-slate-700">Uploading… {progress}%</span>}
              {status === "finalizing" && <span className="font-mono text-indigo-700 animate-pulse">Reassembling & parsing…</span>}
              {status === "done" && <span className="font-mono text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Done</span>}
              {status === "error" && <span className="font-mono text-rose-700">Failed</span>}
            </div>
          </div>
          <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${status === "done" ? "bg-emerald-500" : status === "error" ? "bg-rose-500" : "bg-slate-900"}`}
              style={{ width: `${status === "done" ? 100 : progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-3">
            {status === "uploading" ? (
              <button onClick={cancel} className="text-xs text-rose-600 hover:underline flex items-center gap-1" data-testid="mbox-chunked-cancel">
                <X className="w-3 h-3" />Cancel
              </button>
            ) : <span />}
            {(status === "done" || status === "error") && (
              <button onClick={reset} className="text-xs text-slate-600 hover:text-slate-900" data-testid="mbox-chunked-reset">
                Upload another
              </button>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="mbox-chunked-result">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Parse result</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2 text-xs">
            <div><div className="text-emerald-700">Total bytes</div><div className="font-mono tabular-nums">{fmtSize(result.total_bytes)}</div></div>
            <div><div className="text-emerald-700">Messages</div><div className="font-mono tabular-nums">{result.messages_scanned}</div></div>
            <div><div className="text-emerald-700">Gov contacts</div><div className="font-mono tabular-nums">{result.gov_contacts_found}</div></div>
            <div><div className="text-emerald-700">Threads stored</div><div className="font-mono tabular-nums">{result.messages_stored}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}
